import { useId, useRef, useState, type ReactNode } from "react";
import type { IconName } from "../icons";
import { Button } from "./button";
import { Hint, Input, Label } from "./field";
import { Icon } from "./icon";

/** Outcome of normalising one typed entry; `error` is the (translated) inline message. */
export type ListEntryResult = { ok: true; value: string } | { ok: false; error: string };

export interface ListEditorLabels {
  add: string;
  placeholder?: string;
  /** Shown instead of the list while it is empty. */
  empty: string;
  /** "3 of 20". */
  count: (count: number, max: number) => string;
  /** Accessible name of an entry's remove button, e.g. "Remove worksphr.com". */
  remove: (item: string) => string;
  duplicate: (item: string) => string;
  /** Inline error when the list already holds `max` entries. */
  full: string;
  /** Polite screen-reader announcements. */
  added: (item: string) => string;
  removed: (item: string) => string;
}

export interface ListEditorProps {
  label: ReactNode;
  hint?: ReactNode;
  /** Extra guidance under the list; linked to the input like the hint. */
  note?: ReactNode;
  items: readonly string[];
  onChange: (items: string[]) => void;
  /** Normalise + validate one typed entry (lower-casing, stripping, format rules). */
  parse: (raw: string) => ListEntryResult;
  max: number;
  labels: ListEditorLabels;
  icon?: IconName;
  /** Read-only: entries are listed without the input or remove buttons. */
  disabled?: boolean;
  className?: string;
}

/**
 * Editable list of unique text entries: input + Add (Enter also adds), per-entry validation with an
 * inline error, a labelled remove button per entry, an empty state and an "n of max" count.
 */
export function ListEditor({ label, hint, note, items, onChange, parse, max, labels, icon, disabled = false, className }: ListEditorProps) {
  const id = useId();
  const inputId = `${id}-input`;
  const labelId = `${id}-label`;
  const hintId = `${id}-hint`;
  const countId = `${id}-count`;
  const noteId = `${id}-note`;
  const errorId = `${id}-error`;
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState("");

  const rejection = (value: string): string | null => {
    if (items.includes(value)) return labels.duplicate(value);
    return items.length >= max ? labels.full : null;
  };
  const add = () => {
    const result = parse(draft);
    const problem = result.ok ? rejection(result.value) : result.error;
    inputRef.current?.focus();
    if (!result.ok || problem !== null) {
      setError(problem);
      return;
    }
    onChange([...items, result.value]);
    setDraft("");
    setError(null);
    setNotice(labels.added(result.value));
  };
  const remove = (item: string) => {
    onChange(items.filter((entry) => entry !== item));
    setNotice(labels.removed(item));
    inputRef.current?.focus();
  };
  const describedBy = [hint ? hintId : null, countId, note ? noteId : null, error ? errorId : null].filter(Boolean).join(" ");

  return (
    <div className={className}>
      <div className="flex items-baseline justify-between gap-3">
        {disabled ? (
          <span id={labelId} className="label">
            {label}
          </span>
        ) : (
          <Label id={labelId} htmlFor={inputId}>
            {label}
          </Label>
        )}
        <span id={countId} className="mb-1.5 shrink-0 font-mono text-xs text-fg-subtle">
          {labels.count(items.length, max)}
        </span>
      </div>
      {hint ? (
        <p id={hintId} className="mb-2 text-xs text-fg-subtle">
          {hint}
        </p>
      ) : null}
      {disabled ? null : (
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            id={inputId}
            className="font-mono text-xs"
            value={draft}
            placeholder={labels.placeholder}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            invalid={Boolean(error)}
            aria-describedby={describedBy}
            onChange={(event) => {
              setDraft(event.target.value);
              setError(null);
            }}
            onKeyDown={(event) => {
              if (event.key !== "Enter" || event.nativeEvent.isComposing) return;
              event.preventDefault();
              add();
            }}
          />
          <Button variant="secondary" className="shrink-0" onClick={add}>
            <Icon name="plus" />
            {labels.add}
          </Button>
        </div>
      )}
      {error ? (
        <p id={errorId} role="alert" className="mt-1.5 text-xs font-semibold text-danger">
          {error}
        </p>
      ) : null}
      {items.length === 0 ? (
        <p className="mt-2 rounded-md border border-dashed border-line px-3 py-2 text-xs text-fg-muted">{labels.empty}</p>
      ) : (
        <ul className="mt-2 space-y-2" aria-labelledby={labelId}>
          {items.map((item) => (
            <li key={item} className="flex items-center gap-2 rounded-md border border-line px-3 py-1.5">
              {icon ? <Icon name={icon} className="text-fg-subtle" /> : null}
              <span className="min-w-0 flex-1 truncate font-mono text-xs">{item}</span>
              {disabled ? null : (
                <Button variant="ghost" size="sm" iconOnly aria-label={labels.remove(item)} onClick={() => { remove(item); }}>
                  <Icon name="x" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {note ? <Hint id={noteId}>{note}</Hint> : null}
      <p role="status" className="sr-only">
        {notice}
      </p>
    </div>
  );
}
